/**
 * Image URL Helper Utility
 * Constructs proper image URLs for production deployment
 */

// Get the base URL from environment or use production domain
const getBaseUrl = () => {
  // In production, always use the production domain
  if (process.env.NODE_ENV === 'production') {
    return 'https://zd.sa';
  }

  // In development, check for API URL environment variable
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    // Remove /api suffix if present
    return apiUrl.replace(/\/api$/, '');
  }

  // Fallback to production URL
  return 'https://zd.sa';
};

/**
 * Converts a relative image path to a full URL
 * @param {string} imagePath - Relative path (e.g., "/uploads/images/photo.jpg")
 * @param {string} fallback - Optional fallback image URL
 * @returns {string} Full HTTPS URL or fallback
 */
export const getImageUrl = (imagePath, fallback = null) => {
  // Return null if no path provided and no fallback
  if (!imagePath && !fallback) {
    return null;
  }

  // Return fallback if no path provided
  if (!imagePath) {
    return fallback;
  }

  // If already a full URL (http:// or https://), return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // If relative path, prepend base URL
  const baseUrl = getBaseUrl();

  // Ensure path starts with /
  const path = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;

  return `${baseUrl}${path}`;
};

/**
 * Get placeholder image URL
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @param {string} text - Placeholder text (not used, kept for API compatibility)
 * @returns {string} Local placeholder image path
 */
export const getPlaceholderUrl = (width = 600, height = 400, text = 'Image') => {
  // Map dimensions to appropriate local placeholder SVG
  if (width === 800 && height === 500) {
    return '/images/placeholder-project-large.svg';
  } else if (width === 400 && height === 300) {
    return '/images/placeholder-project.svg';
  } else if (width === 400 && height === 250) {
    return '/images/placeholder-blog.svg';
  } else if (width === 100 && height === 100) {
    return '/images/placeholder-blog-thumb.svg';
  } else if (width === 600 && height === 400) {
    return '/images/placeholder-about.svg';
  }

  // Default fallback for any other dimensions
  return '/images/placeholder-project.svg';
};

export default getImageUrl;
